import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { GradientOutline } from 'modules/ai/ui/animations/GradientOutline';
import { NODE_TYPES } from 'modules/workflows/constants';
import {
  getSelectedAINodes,
  getSelectedNodes,
  getNodes,
  selectGenAINodes,
} from 'modules/workflows/ducks';
import { useJourneyAssistContext } from 'modules/workflows/context/JourneyAssistContext';
import { AiSparkle24 } from 'ui/icons';
import { useJourneyReviewContext } from 'modules/workflows/context/JourneyReviewContext';
import { HelperBanner } from './HelperBanner';
import {
  ERROR_MESSAGES,
  PROMPT_DEFAULT,
  PROMPT_FIELD,
  ToggleType,
  PromptType,
} from './JourneyAssist.constants';
import { StyledDialog, StyledJourneyAssistClosed } from './JourneyAssistDialog.styled';
import { DefaultHeader } from './DefaultHeader';
import { LoadingHeader } from './LoadingHeader';
import { Loading } from './Loading';
import { PromptEdit } from './PromptEdit';
import { ToolbarHeader } from './ToolbarHeader';
import { useFetchAndInsertAIGeneratedTiles } from './hooks/useFetchAndInsertAIGeneratedTiles';
import { replaceBullets } from './utils/PromptUtils';

export const DIALOG_TEST_ID = 'journey-assist-dialog';
export const DIALOG_CLOSED_TEST_ID = 'journey-assist-closed';

export const JourneyAssistDialog = () => {
  const dispatch = useDispatch();
  const {
    isOpen,
    setIsOpen,
    addToPathTileId,
    promptType,
    shouldShowHighlight,
    onHighlightDialog,
    setIsJourneyAssistActive,
  } = useJourneyAssistContext();
  const [addOrReplaceToggle, setAddOrReplaceToggle] = useState(ToggleType.Add);
  const [initialAddOrReplace, setInitialAddOrReplace] = useState<ToggleType>();
  const [isGenerating, setIsGenerating] = useState(false);
  const [bannerMsg, setBannerMsg] = useState('');

  const selectedAINodes = useSelector(getSelectedAINodes);
  const nodes = useSelector(getNodes);
  const selectedNodes = useSelector(getSelectedNodes);

  const { isOpen: isJourneyReviewOpen, setIsOpen: setIsJourneyReviewOpen } =
    useJourneyReviewContext();

  const journeyHasSelectedAINodes = selectedAINodes.length > 0;
  const onlyStartTileIsSelected =
    selectedNodes.length === 1 && selectedNodes[0].type === NODE_TYPES.START && addToPathTileId;

  useEffect(() => {
    if (nodes.length === 1 && onHighlightDialog) {
      setIsOpen(true);
      onHighlightDialog();
    }
  }, [nodes.length]);

  /** React hook form */
  const { setValue, watch } = useFormContext();
  const [prompt] = watch([PROMPT_FIELD]);

  useEffect(() => {
    if (prompt.length && bannerMsg === ERROR_MESSAGES.EMPTY_PROMPT) {
      setBannerMsg('');
    }
  }, [prompt, bannerMsg]);

  useEffect(() => {
    if (isOpen && isJourneyReviewOpen) {
      setIsOpen(false);
    }
  }, [isOpen, isJourneyReviewOpen]);

  const handleFetchTilesError = (message = ERROR_MESSAGES.DEFAULT) => {
    setBannerMsg(message);
    setIsGenerating(false);
    if (!journeyHasSelectedAINodes) setIsJourneyAssistActive(false);
  };

  const handleFetchTilesSuccess = () => {
    setIsGenerating(false);
  };

  const getAddOrReplaceType = () => {
    if (promptType === PromptType.NewJourney || !selectedNodes.length) return;
    if (onlyStartTileIsSelected) return ToggleType.Add;
    if (addToPathTileId) return addOrReplaceToggle;
    return ToggleType.Replace;
  };

  const { cancelFetchAndInsertGenAITiles, fetchAndInsertGenAITiles, updateAIGeneratedTiles } =
    useFetchAndInsertAIGeneratedTiles({
      onFetchTilesError: handleFetchTilesError,
      onFetchTilesSuccess: handleFetchTilesSuccess,
    });

  /** Handle fetch generated tiles query */
  const handleFetch = () => {
    if (!prompt.length) {
      setBannerMsg(ERROR_MESSAGES.EMPTY_PROMPT);
      return;
    }
    setBannerMsg('');
    setIsGenerating(true);
    setIsJourneyAssistActive(true);

    if (!journeyHasSelectedAINodes) setInitialAddOrReplace(getAddOrReplaceType());
    fetchAndInsertGenAITiles({
      prompt: replaceBullets(prompt),
      ...(!journeyHasSelectedAINodes && { addOrReplace: getAddOrReplaceType() }),
    });
  };

  /** Handle cancel fetch generated tiles query */
  const handleCancel = () => {
    cancelFetchAndInsertGenAITiles();
    setIsGenerating(false);
    if (!journeyHasSelectedAINodes) setIsJourneyAssistActive(false);
  };

  /** Handle reset dialog to minimized sta†e */
  const handleResetDialog = () => {
    setBannerMsg('');
    setIsOpen(false);
    setValue(PROMPT_FIELD, PROMPT_DEFAULT);
    setIsJourneyAssistActive(false);
  };

  if (!isOpen) {
    return (
      <StyledJourneyAssistClosed
        $moveAssitButton={isJourneyReviewOpen}
        data-test={DIALOG_CLOSED_TEST_ID}
        onClick={() => {
          setIsOpen(true);
          setIsJourneyReviewOpen(false);
        }}
      >
        <AiSparkle24 />
        <span>Generate</span>
      </StyledJourneyAssistClosed>
    );
  }

  return (
    <StyledDialog data-test={DIALOG_TEST_ID}>
      {shouldShowHighlight && <GradientOutline />}
      {isGenerating && <LoadingHeader onCancel={handleCancel} />}
      {journeyHasSelectedAINodes && !isGenerating && (
        <ToolbarHeader
          onKeep={() => {
            dispatch(selectGenAINodes([]));
            handleResetDialog();
          }}
          onDelete={() => {
            updateAIGeneratedTiles();
            handleResetDialog();
          }}
          selectedAINodes={selectedAINodes.length}
          addOrReplace={initialAddOrReplace}
        />
      )}
      {!journeyHasSelectedAINodes && !isGenerating && (
        <DefaultHeader
          onCloseDialog={() => setIsOpen(false)}
          showToggle={!!addToPathTileId && !onlyStartTileIsSelected}
          addOrReplace={getAddOrReplaceType()}
        />
      )}
      <PromptEdit
        onFetchAIGeneratedTiles={handleFetch}
        selectedAINodes={selectedAINodes.length}
        setAddOrReplaceToggle={setAddOrReplaceToggle}
        addOrReplace={getAddOrReplaceType()}
        showToggle={!!addToPathTileId && !onlyStartTileIsSelected}
        isEdit={!isGenerating}
      />
      {bannerMsg && <HelperBanner bannerMsg={bannerMsg} />}
      {isGenerating && <Loading />}
    </StyledDialog>
  );
};
